// Use type-only imports for types that are only used as types
import type { DispatchableTechList, DispatchCallList, FirstCallSectorList } from '@kalos/kalos-rpc';
import { DispatchableTech, DispatchCall, FirstCallSector } from '@kalos/kalos-rpc';
import { useQuery } from '@tanstack/react-query';

import { DispatchClientService, timestamp } from '../../tools/helpers';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

export type DispatchableTechFilter = EntityFilter<DispatchableTech>;
export type DispatchCallFilter = {
  /** Filter by department ID */
  departmentId?: number;
  /** Filter by multiple department IDs */
  departmentIdList?: string;
  /** Filter by job type ID */
  jobTypeId?: number;
  /** Filter by multiple job type IDs */
  jobTypeIdList?: string;
  /** Filter by trade ID (stored event trade, falls back to the department default) */
  eventTradeId?: number;
  /** Filter by multiple trade IDs */
  eventTradeIdList?: string;
  /** Filter by sector group (new system: 1=Residential, 2=Commercial) */
  sectorGroup?: number;
  /** Filter by multiple sector groups */
  sectorGroupList?: string;
  /** Filter by residential status (old system: 0=Commercial, 1=Residential) */
  isResidential?: number;
  /** Filter by date range */
  dateRange?: string[];
  /** Filter by date target fields */
  dateTarget?: string[];
  /** Filter by date started */
  dateStarted?: string;
  /** Filter by time started */
  timeStarted?: string;
  /** Filter by date ended */
  dateEnded?: string;
  /** Filter by time ended */
  timeEnded?: string;
  /** Additional fields from EntityFilter */
  fieldMask?: string[];
  pageNumber?: number;
};

export const getDispatchableTechsQueryKeys = (filter: DispatchableTechFilter) => [
  queryKeys.dispatch.root,
  queryKeys.dispatch.dispatchableTechs,
  queryKeys.dispatch.getDispatchableTechsHash?.(filter) ?? JSON.stringify(filter),
];

export const getDispatchCallsQueryKeys = (filter: DispatchCallFilter) => [
  queryKeys.dispatch.root,
  queryKeys.dispatch.dispatchCalls,
  queryKeys.dispatch.getDispatchCallsHash?.(filter) ?? JSON.stringify(filter),
];

export const getDispatchableTechsQueryConfig = (filter: DispatchableTechFilter) => ({
  queryKey: getDispatchableTechsQueryKeys(filter),
  queryFn: async () => {
    const req = DispatchableTech.create({
      ...filter,
      timesheetWindowHours: 12,
      timesheetWindowDatetime: timestamp(),
    });
    return await DispatchClientService.GetDispatchableTechnicians(req);
  },
});

export const getDispatchCallsQueryConfig = (filter: DispatchCallFilter) => ({
  queryKey: getDispatchCallsQueryKeys(filter),
  queryFn: async () => {
    // Create a clean filter object by removing undefined values
    const cleanFilter = Object.fromEntries(
      Object.entries(filter).filter(([_, value]) => value !== undefined),
    );

    // Convert arrays to comma-separated strings for multi-value fields
    if (filter.departmentIdList) {
      cleanFilter.departmentIdList = filter.departmentIdList;
    }
    if (filter.jobTypeIdList) {
      cleanFilter.jobTypeIdList = filter.jobTypeIdList;
    }
    if (filter.eventTradeIdList) {
      cleanFilter.eventTradeIdList = filter.eventTradeIdList;
    }
    if (filter.sectorGroupList) {
      cleanFilter.sectorGroupList = filter.sectorGroupList;
    }

    const req = DispatchCall.create(cleanFilter);
    const res = await DispatchClientService.GetDispatchCallsOld(req);
    return res;
  },
});

export const useDispatchableTechsQuery = <TSelectData = DispatchableTechList,>({
  filter = {},
  enabled = true,
  select,
  staleTime,
}: {
  filter?: DispatchableTechFilter;
  enabled?: boolean;
  select?: (
    data: Awaited<ReturnType<typeof DispatchClientService.GetDispatchableTechnicians>>,
  ) => TSelectData;
  staleTime?: number;
} = {}) => {
  return useQuery({
    ...getDispatchableTechsQueryConfig(filter),
    enabled,
    select,
    staleTime,
    // Keep prior results while filter/queryKey changes so Dispatch UI tables stay mounted
    // and local filter state (Jobs Remaining / Technicians) is not reset.
    placeholderData: (previousData) => previousData,
  });
};

export const useDispatchCallsQuery = <TSelectData = DispatchCallList,>({
  filter = {},
  enabled = true,
  select,
}: {
  /** Filter criteria for dispatch calls */
  filter?: DispatchCallFilter;
  /** Whether the query should be enabled */
  enabled?: boolean;
  /** Optional function to transform the query data */
  select?: (
    data: Awaited<ReturnType<typeof DispatchClientService.GetDispatchCalls>>,
  ) => TSelectData;
} = {}) => {
  return useQuery({
    ...getDispatchCallsQueryConfig(filter),
    enabled,
    select,
    placeholderData: (previousData) => previousData,
  });
};

export const useFirstCallSectorsQuery = <TSelectData = FirstCallSectorList,>({
  enabled = true,
  select,
}: {
  enabled?: boolean;
  select?: (
    data: Awaited<ReturnType<typeof DispatchClientService.BatchGetFirstCallSector>>,
  ) => TSelectData;
} = {}) => {
  return useQuery({
    queryKey: [queryKeys.dispatch.root, queryKeys.dispatch.firstCallSectors],
    queryFn: () => DispatchClientService.BatchGetFirstCallSector(FirstCallSector.create({})),
    enabled,
    select,
  });
};
